import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface PopoverProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  onDismiss: () => void;
  children: ReactNode;
  className?: string;
  // Which edge of the panel lines up with the anchor.
  align?: "start" | "end";
  role?: string;
  labelledBy?: string;
  label?: string;
  id?: string;
  activeDescendant?: string;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  autoFocus?: boolean;
}

const GAP = 6;
const MARGIN = 12;
// A panel is never squeezed below this; it flips or scrolls instead.
const MIN_ROOM = 180;

// A panel that must sit above everything and must not be cut off. It is
// portalled out of the flow, because an ancestor with `overflow: auto` (the
// modal body, a tab strip) clips an absolutely positioned child no matter
// what its z-index says. Inside a <dialog> the portal target is that dialog,
// so the panel stays in the browser's top layer with it.
export function Popover({
  anchorRef, open, onDismiss, children, className = "", align = "start",
  role, labelledBy, label, id, activeDescendant, onKeyDown, autoFocus = false,
}: PopoverProps) {
  const panel = useRef<HTMLDivElement>(null);
  const [host, setHost] = useState<Element | null>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ visibility: "hidden" });

  const place = useCallback(() => {
    const trigger = anchorRef.current?.getBoundingClientRect();
    const box = panel.current?.getBoundingClientRect();
    if (!trigger || !box) return;
    // Decide the side from the room each one offers, then cap the height to
    // that room, so the panel can never run off the top or the bottom.
    const below = window.innerHeight - trigger.bottom - GAP - MARGIN;
    const above = trigger.top - GAP - MARGIN;
    const flipUp = below < Math.min(box.height, MIN_ROOM) && above > below;
    const maxHeight = Math.max(MIN_ROOM, flipUp ? above : below);
    const height = Math.min(box.height, maxHeight);
    const top = flipUp ? Math.max(MARGIN, trigger.top - GAP - height) : trigger.bottom + GAP;
    let left = align === "end" ? trigger.right - box.width : trigger.left;
    left = Math.min(Math.max(MARGIN, left), Math.max(MARGIN, window.innerWidth - box.width - MARGIN));
    setStyle({
      position: "fixed", top, left, maxHeight,
      minWidth: align === "end" ? undefined : trigger.width,
    });
  }, [anchorRef, align]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    // Inside a <dialog> the panel must live in that dialog to stay in the
    // browser's top layer; elsewhere the document body is the highest host.
    setHost(anchorRef.current?.closest("dialog") ?? document.body);
    place();
    // The first pass measures the panel unconstrained; the second runs once
    // the capped height is in effect, so the final position is the true one.
    const settle = requestAnimationFrame(place);
    if (autoFocus) panel.current?.focus();
    // Anything that moves the anchor must move the panel with it.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      cancelAnimationFrame(settle);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place, autoFocus, anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    function onPointer(event: MouseEvent) {
      const target = event.target as Node;
      if (panel.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onDismiss();
    }
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open, anchorRef, onDismiss]);

  if (!open || !host) return null;
  return createPortal(
    <div
      className={`popover ${className}`} ref={panel} style={style} id={id} role={role}
      aria-labelledby={labelledBy} aria-label={label} aria-activedescendant={activeDescendant}
      tabIndex={autoFocus ? -1 : undefined} onKeyDown={onKeyDown}
    >
      {children}
    </div>,
    host,
  );
}
